import { ACTION, useSecuritiesContext } from '@talos/kyoko';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useRoleAuth } from '../../../hooks/useRoleAuth';
import { useTradingSettings } from '../../../providers/TradingSettingsContext';
import type { PanelType } from '../../../types/LayoutConfig';
import { PANEL_COMPONENTS_LIST } from '../Panels';
import { hasOptionsTradingAccess } from '../utils/hasOptionsTradingAccess';

export function useEnabledPanelTypes() {
  const featureFlags = useFeatureFlag();
  const {
    enablePriceGrid,
    enableAutoHedging,
    enableCareOrderManagement,
    enableTakerMarketplaceETFRFQFlow,
    enableMakerMarketplaceETFRFQFlow,
    enableReconMismatchesBlotter,
    enableAccountLedgerEventsPage,
    enableAdditionalTradingPanels,
    enableLadderPanels,
    enableOTCSupport,
    enableCreditBlotter,
    enableBalancesBlotter,
    enableTransfersBlotter,
    enableSignals,
    viewDealerAdminAndBuyingPowerMenuItems,
  } = featureFlags;
  const { options } = useSecuritiesContext();
  const { useTradeAllocations } = useTradingSettings();
  const { isAuthorized } = useRoleAuth();
  const userHasOptionsTradingAccess = hasOptionsTradingAccess({
    flags: featureFlags,
    securities: { options },
  });
  const enabledPanelTypes = useMemo(
    () =>
      PANEL_COMPONENTS_LIST.filter(panel => {
        const priceGridPanels: PanelType[] = ['priceGrid', 'multilegPriceGrid'] as const;
        if (!enablePriceGrid && priceGridPanels.includes(panel.type)) {
          return false;
        }

        const autoHedgingPanels: PanelType[] = ['positionAutoHedgingRules', 'hedgePositionStatus'] as const;
        if (!enableAutoHedging && autoHedgingPanels.includes(panel.type)) {
          return false;
        }

        const reconMismatchPanels: PanelType[] = ['reconMismatches', 'reconMismatchesDetails'] as const;
        if (!enableReconMismatchesBlotter && reconMismatchPanels.includes(panel.type)) {
          return false;
        }

        if (!enableAccountLedgerEventsPage && panel.type === 'accountLedgerEvents') {
          return false;
        }

        if (!enableTakerMarketplaceETFRFQFlow && panel.type === 'careOrders') {
          return false;
        }

        if (!enableCareOrderManagement && panel.type === 'careOrderManagement') {
          return false;
        }

        if (!enableMakerMarketplaceETFRFQFlow && panel.type === 'manualRfqs') {
          return false;
        }

        if (!useTradeAllocations && panel.type === 'allocations') {
          return false;
        }

        if (!userHasOptionsTradingAccess && panel.type === 'options') {
          return false;
        }

        if (!enableTransfersBlotter && panel.type === 'transfersBlotter') {
          return false;
        }

        const additionalTradingPanels: PanelType[] = [
          'historicalPriceChart',
          'orderBook',
          'multilegOrderBook',
          'deepDive',
          'tradesHistory',
        ] as const;
        if (!enableAdditionalTradingPanels && additionalTradingPanels.includes(panel.type)) {
          return false;
        }

        const dealProductBuilderPanels: PanelType[] = ['otcDerivatives', 'loans', 'dealProductBuilder'] as const;
        if (!enableOTCSupport && dealProductBuilderPanels.includes(panel.type)) {
          return false;
        }

        const ladderPanels: PanelType[] = ['volumeLadder', 'priceLadder'] as const;
        if (!enableLadderPanels && ladderPanels.includes(panel.type)) {
          return false;
        }

        if (!enableCreditBlotter && panel.type === 'creditBlotter') {
          return false;
        }

        if (!enableBalancesBlotter && panel.type === 'balancesBlotter') {
          return false;
        }

        if (!enableSignals && panel.type === 'marketCapEstimated') {
          return false;
        }

        const dealerPanels: PanelType[] = ['customerOrdersBlotter', 'customerTradesBlotter'] as const;
        if (!isAuthorized(ACTION.VIEW_DEALER) && dealerPanels.includes(panel.type)) {
          return false;
        }

        const dealerBuyingPowerPanels: PanelType[] = ['customerBalancesBlotter', 'customerTransfersBlotter'] as const;
        if (
          (!isAuthorized(ACTION.VIEW_DEALER) || !viewDealerAdminAndBuyingPowerMenuItems) &&
          dealerBuyingPowerPanels.includes(panel.type)
        ) {
          return false;
        }

        return true;
      }),
    [
      enablePriceGrid,
      enableAutoHedging,
      enableCareOrderManagement,
      enableReconMismatchesBlotter,
      enableAccountLedgerEventsPage,
      enableTakerMarketplaceETFRFQFlow,
      enableMakerMarketplaceETFRFQFlow,
      useTradeAllocations,
      userHasOptionsTradingAccess,
      enableAdditionalTradingPanels,
      enableOTCSupport,
      enableLadderPanels,
      enableCreditBlotter,
      enableBalancesBlotter,
      enableTransfersBlotter,
      enableSignals,
      viewDealerAdminAndBuyingPowerMenuItems,
      isAuthorized,
    ]
  );
  return enabledPanelTypes;
}
